// The cards beside a lead's facts: the next follow-up, the owner, do-not-contact, and — once converted — the organisation it became.

import type { Lead } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, SelectField, Switch, TextField, useToast } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useNow } from '../../floor/live';
import { useProblem } from '../../api/use-problem';
import { Notice, Section } from '../../ui';
import { organizationParams } from '../organizations/queries';
import { HQ_PATHS } from '../paths';
import { useLeadAssignees, useUpdateLead } from './queries';
import { followUpIn, fromLocalInput, toLocalInput } from './when';

const NO_OWNER = 'none';

export function FollowUpCard({ lead }: { readonly lead: Lead }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const problems = useProblem();
  const { show } = useToast();
  const update = useUpdateLead();
  const now = useNow(60_000);
  // The picker follows the saved date until someone types in it.
  const saved = lead.nextFollowUpAt === null ? '' : toLocalInput(lead.nextFollowUpAt);
  const [draft, setDraft] = useState<{ readonly of: string; readonly value: string } | undefined>(undefined);
  const value = draft !== undefined && draft.of === saved ? draft.value : saved;
  const overdue = lead.nextFollowUpAt !== null && new Date(lead.nextFollowUpAt).getTime() < now;

  const save = (instant: string | null) =>
    update.mutate(
      { leadId: lead.id, body: { nextFollowUpAt: instant } },
      {
        onSuccess: () => {
          setDraft(undefined);
          show({ tone: 'ok', title: t(instant === null ? 'leads.lead.followUp.cleared' : 'leads.lead.followUp.saved') });
        },
        onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('leads.lead.followUp.failed') }) }),
      },
    );

  return (
    <Section title={t('leads.lead.followUp.title')}>
      <div className="console-form">
        <p className={overdue ? 'console-lead-card__followup--overdue' : 'console-muted'}>
          {lead.nextFollowUpAt === null ? t('leads.lead.followUp.none') : overdue ? t('leads.lead.followUp.overdue', { when: format.relative(lead.nextFollowUpAt) }) : format.dateTime(lead.nextFollowUpAt, { date: 'medium', time: 'short' })}
        </p>
        <TextField label={t('leads.lead.followUp.label')} type="datetime-local" value={value} onChange={(event) => setDraft({ of: saved, value: event.target.value })} />
        <div className="console-hq-chips">
          <Button size="sm" variant="secondary" onClick={() => save(followUpIn(1, Date.now()))}>
            {t('leads.lead.followUp.tomorrow')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => save(followUpIn(3, Date.now()))}>
            {t('leads.lead.followUp.in3')}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => save(followUpIn(7, Date.now()))}>
            {t('leads.lead.followUp.nextWeek')}
          </Button>
          <Button size="sm" variant="ghost" disabled={lead.nextFollowUpAt === null} onClick={() => save(null)}>
            {t('leads.lead.followUp.clear')}
          </Button>
        </div>
        <div className="console-form__actions">
          <Button size="sm" variant="primary" disabled={draft === undefined || fromLocalInput(value) === undefined} busy={update.isPending} onClick={() => save(fromLocalInput(value) ?? null)}>
            {t('leads.lead.followUp.save')}
          </Button>
        </div>
      </div>
    </Section>
  );
}

export function OwnerCard({ lead }: { readonly lead: Lead }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const assignees = useLeadAssignees();
  const update = useUpdateLead();
  return (
    <Section title={t('leads.lead.owner.title')}>
      <SelectField
        label={t('leads.lead.owner.label')}
        options={[{ value: NO_OWNER, label: t('leads.lead.owner.nobody') }, ...(assignees.data?.data ?? []).map((one) => ({ value: one.id, label: one.name }))]}
        value={lead.ownerUserId ?? NO_OWNER}
        disabled={update.isPending}
        onValueChange={(value) =>
          update.mutate(
            { leadId: lead.id, body: { ownerUserId: value === NO_OWNER ? null : value } },
            {
              onSuccess: () => show({ tone: 'ok', title: t('leads.lead.owner.saved') }),
              onError: (error) => show({ tone: 'danger', title: problems.text(error, { fallback: t('leads.lead.owner.failed') }) }),
            },
          )
        }
      />
    </Section>
  );
}

export function DoNotContactCard({ lead, onToggle }: { readonly lead: Lead; readonly onToggle: (mode: 'set' | 'clear') => void }): ReactNode {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  return (
    <Section title={t('leads.lead.dnc.title')}>
      <div className="console-form">
        <p className="console-muted">{t('leads.lead.dnc.description')}</p>
        <Switch label={t('leads.lead.dnc.on')} checked={lead.doNotContact} onCheckedChange={(on) => onToggle(on ? 'set' : 'clear')} />
        {lead.doNotContact ? (
          <Notice tone="warn">
            {lead.doNotContactReason}
            {lead.doNotContactAt === null ? null : <span className="console-hq-secondary">{t('leads.lead.dnc.since', { when: format.relative(lead.doNotContactAt) })}</span>}
          </Notice>
        ) : null}
      </div>
    </Section>
  );
}

export function ConvertedCard({ lead }: { readonly lead: Lead }): ReactNode {
  const { t } = useTranslation('hq');
  if (lead.organizationId === null) return null;
  return (
    <Section title={t('leads.lead.converted.title')}>
      <div className="console-form">
        <p className="console-muted">{t('leads.lead.converted.body', { name: lead.organizationName ?? lead.businessName })}</p>
        <Button asChild variant="secondary">
          <Link to={HQ_PATHS.organization} params={organizationParams(lead.organizationId)}>
            {t('leads.lead.converted.open')}
          </Link>
        </Button>
      </div>
    </Section>
  );
}
